import React, { useContext, useEffect, useRef, useState } from 'react'
import UserContext from '../context/UserContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import Skeleton, { SkeletonScreen } from '../components/Skeleton';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet";
import LandingNav from '../components/landing/LandingNav';
import LandingFooter from '../components/landing/LandingFooter';
import useLandingTheme from '../components/landing/useLandingTheme';
import { menuUrl } from '../menu.js';
import '../landing.css';

// Mercado Pago redirige acá después de suscribirse. La suscripción la activa el
// webhook de n8n (n8n/miqrapp-suscripciones.js). Como respaldo, por si el aviso de MP
// no llega, la página le pide a n8n que consulte el estado en MP unas veces mientras espera.
const VERIFICAR_URL = "https://n8n.miqrapp.com/webhook/miqrapp/verificar";
const VERIFICAR_ESPERAS = [0, 5000, 15000, 30000, 60000];
// Pasado este tiempo sin confirmación se avisa que está tardando (se sigue escuchando igual).
const DEMORA = 75000;

const PRO_PERKS = [
    "Productos ilimitados",
    "Tu menú sin la marca de MiQRapp",
    "Todo lo del plan Gratis"
];

const Check = () => (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7"></path></svg>
);

const Checkout = () => {
    const { LSuser } = useContext(UserContext);

    const [userData, setUserData] = useState();
    const [demorado, setDemorado] = useState(false);
    const activa = useRef(false);
    const { dark, toggleTheme } = useLandingTheme();

    useEffect(() => {
        const timeout = setTimeout(() => setDemorado(true), DEMORA);
        return () => clearTimeout(timeout);
    }, [])

    useEffect(() => {
        const unsubscribe = onSnapshot(doc(db, "users", LSuser.email), (snapshot) => {
            setUserData(snapshot.data());
            activa.current = !!snapshot.data()?.subscribed;
        });

        return () => unsubscribe();
    }, [])

    useEffect(() => {
        let cancelado = false;
        const timeouts = [];

        const verificar = async () => {
            if (cancelado || activa.current || !auth.currentUser) return;
            try {
                await fetch(VERIFICAR_URL, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ idToken: await auth.currentUser.getIdToken() })
                });
            } catch (error) {
                console.error(error);
            }
        };

        // Al volver de MP la sesión de Firebase puede tardar en restaurarse.
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (!user || timeouts.length) return;
            VERIFICAR_ESPERAS.forEach((espera) => timeouts.push(setTimeout(verificar, espera)));
        });

        return () => {
            cancelado = true;
            unsubscribe();
            timeouts.forEach(clearTimeout);
        };
    }, [])

  const subscribed = !!userData?.subscribed;

  return (
    <div className="lp" data-theme={dark ? "dark" : "light"}>
        <Helmet>
            <title>Suscripción - MiQRapp</title>
            <body className={dark ? "lp-body lp-body-dark" : "lp-body"} />
            <meta name="theme-color" content={dark ? "#120b14" : "#fbfaf7"} />
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <LandingNav dark={dark} onToggleTheme={toggleTheme}></LandingNav>

        <main className="lp-checkout">
            <div className="lp-hero-bg" aria-hidden="true">
                <span className="lp-blob lp-blob-1"></span>
                <span className="lp-blob lp-blob-2"></span>
            </div>

            {subscribed ?

            <section className="lp-checkout-card lp-hero-intro" aria-live="polite">
                <span className="lp-checkout-badge lp-checkout-badge-ok" aria-hidden="true"><Check></Check></span>
                <span className="lp-eyebrow">Plan Pro activo</span>
                <h1>¡Listo{userData.nombreEmpresa ? `, ${userData.nombreEmpresa}` : ""}! Ya sos Pro</h1>
                <p className="lp-lead">Tu suscripción quedó confirmada. Esto es lo que se activó en tu menú:</p>
                <ul className="lp-checkout-perks">
                    {PRO_PERKS.map((perk) => <li key={perk}><Check></Check>{perk}</li>)}
                </ul>
                <div className="lp-checkout-actions">
                    <Link to="/cuenta" className="lp-btn lp-btn-primary">Ir a mi panel</Link>
                    {userData.usuarioEmpresa &&
                        <a href={menuUrl(userData.usuarioEmpresa)} target="_blank" rel="noreferrer" className="lp-btn lp-btn-secondary">Ver mi menú</a>
                    }
                </div>
            </section>

            :

            <section className="lp-checkout-card lp-hero-intro" aria-live="polite">
                <span className="lp-eyebrow">Suscripción Pro</span>
                <h1>{demorado ? "Está tardando más de lo normal" : "Estamos confirmando tu pago"}</h1>
                <p className="lp-lead">
                    {demorado ?
                        "Si ya pagaste, la suscripción se activa sola en unos minutos y te va a aparecer en el panel. Si cancelaste el pago, podés volver a intentarlo desde tu cuenta." :
                        "Mercado Pago nos avisa apenas se aprueba. Suele tardar unos segundos, no hace falta que recargues la página."}
                </p>

                {/* Esqueleto de la confirmación que aparece cuando Mercado Pago aprueba. */}
                <SkeletonScreen label="Confirmando el pago…" className="lp-checkout-skel">
                    {PRO_PERKS.map((perk, i) => (
                        <span key={perk} className="lp-checkout-skel-row">
                            <Skeleton width={18} height={18} circle></Skeleton>
                            <Skeleton width={["70%", "85%", "55%"][i]} height={14}></Skeleton>
                        </span>
                    ))}
                </SkeletonScreen>

                <div className="lp-checkout-actions">
                    <Link to="/cuenta" className={demorado ? "lp-btn lp-btn-primary" : "lp-btn lp-btn-secondary"}>Ir a mi cuenta</Link>
                    {demorado && <a href="mailto:contacto@miqrapp.com?subject=Suscripci%C3%B3n%20Pro" className="lp-btn lp-btn-secondary">Escribinos</a>}
                </div>
            </section>
            }
        </main>

        <LandingFooter></LandingFooter>
    </div>
  )
}

export default Checkout
